/**
 * Hand-rolled mirror of `backend/src/data-models/`. Keep in sync with the
 * backend interfaces — there is no shared package between the two.
 */

export interface BaseDataModel {
  id: number;
  createdAt: string;
  updatedAt: string;
}

export interface BaseTenantDataModel extends BaseDataModel {
  tenant_id: number;
}

// --- identity / tenant ------------------------------------------------------

export interface Tenant extends BaseDataModel {
  name: string;
}

export interface MyTenantOwner {
  id: number;
  name: string | null;
  email: string;
}

/** Shape of `GET /my/tenant` — the caller's own active tenant, self-service view. */
export interface MyTenant {
  id: number;
  name: string;
  createdAt: string;
  owners: MyTenantOwner[];
}

export interface User extends BaseDataModel {
  email: string;
  name: string | null;
  status: 'PENDING' | 'ACTIVE';
  last_login_at: string | null;
}

// --- auth helpers (shape returned by /auth/login + switch-tenant) -----------

export interface UserPrincipal {
  id: number;
  email: string;
  name: string | null;
  tenantId: number;
  role: string;
  roles: string[];
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

export interface UserMembership {
  tenantId: number;
  tenantName: string;
  role: string;
}

/** Shape that comes back wrapped in `{ data }` from /auth/login. */
export interface LoginPayload {
  user: {
    userId: number;
    tenantId: number;
    email: string;
    name?: string | null;
    roles: string[];
  };
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  /** Every auth response carries the caller's workspaces (guest rows excluded). */
  memberships?: UserMembership[];
}

// --- example domain ---------------------------------------------------------

export interface Tag extends BaseDataModel {
  name: string;
  color: string | null;
}

/** Mirrors the backend `ProjectType` enum — which of the core use cases a project follows. */
export type ProjectType = 'IDEA_VALIDATION' | 'BUSINESS_VALIDATION' | 'STOCK_VALIDATION' | 'STRATEGY_MANAGEMENT';

export interface Project extends BaseTenantDataModel {
  name: string;
  projectType: ProjectType;
  description: string | null;
  /** The story the user wrote when creating the project (Markdown). */
  userPrompt: string | null;
  websiteUrls: string[];
  githubUrl: string | null;
  docsUrl: string | null;
  linkedinUrl: string | null;
  generateUserPrompt: string | null;
  /** STOCK_VALIDATION: the stock's Yahoo Finance symbol (AAPL, 7203.T), set by the backend from the symbol and exchange typed. */
  symbol: string | null;
  /** STOCK_VALIDATION: the exchange as the user named it (NASDAQ). */
  exchange: string | null;
}

export type ProjectTypeInputRule = 'required' | 'optional' | 'hidden';

/**
 * Per-project-type onboarding rules (design plan §3.2): which wizard inputs
 * exist, which are required, and which document is generated first. Served
 * from the console (`modules/onboarding/projectTypeProfiles.ts`) until the
 * backend exposes `GET /project-types` with this same shape.
 */
export interface ProjectTypeProfile {
  type: ProjectType;
  name: string;
  tagline: string;
  /** Who this path is for — shown on the picker card after "For". */
  audience: string;
  inputs: {
    description: 'required' | 'optional';
    websiteUrl: ProjectTypeInputRule;
    githubUrl: 'optional' | 'hidden';
    docsUrl: 'optional' | 'hidden';
    linkedinUrl: 'optional' | 'hidden';
  };
  /** `DocumentType.label` generated first: the first validation stage, or PRODUCT_ANALYSIS for a type without a guided validation. */
  kickoffDocumentType: string;
  /** The document category whose root types are this type's validation stages; null for a type without a guided validation. */
  validationCategory: string | null;
  /**
   * How "Start validation" begins: generate the kickoff document from the
   * description straight away, or analyze the project's URLs into a company
   * brief first (the analysis then chains the first stage itself).
   */
  start: 'generate-kickoff' | 'analyze-sources';
}

export interface ProjectNote extends BaseTenantDataModel {
  projectId: number;
  body: string;
}

/**
 * Configures which entity types are shown in a project's "Main Entities"
 * dashboard block, and their display order. `entityType` matches
 * `Entity.entityType`. The same `entityType` may appear in
 * more than one tab (each as its own row).
 */
export interface ProjectMonitoringEntityType extends BaseTenantDataModel {
  projectId: number;
  entityType: string;
  order: number;
  /** Which ProjectMonitoringEntityTab groups this row in the UI. `null` = the implicit "General" tab. */
  tabId: number | null;
}

/**
 * A user-defined tab that groups ProjectMonitoringEntityType rows in a
 * project's "Main Entities" dashboard block. Purely a UI grouping — the
 * "General" tab is not a row here, it's the implicit `tabId: null` bucket,
 * always shown first and not deletable.
 */
export interface ProjectMonitoringEntityTab extends BaseTenantDataModel {
  projectId: number;
  name: string;
  /** MUI icon name — frontend concern only, same convention as AiAssistantRole.avatarIcon. */
  icon: string | null;
  order: number;
}

/**
 * Configures which DocumentDashboardCard rows are shown in a project's
 * "High Level Overview" dashboard block, and their display order. The same
 * card may appear in more than one tab (each as its own row).
 */
export interface ProjectDashboardCardAssignment extends BaseTenantDataModel {
  projectId: number;
  documentDashboardCardId: number;
  order: number;
  /** How many of the block's 4 grid columns this card spans (1-4). */
  columnSpan: number;
  /** Which ProjectDashboardCardTab groups this row in the UI. `null` = the implicit "General" tab. */
  tabId: number | null;
}

/**
 * A user-defined tab that groups ProjectDashboardCardAssignment rows in a
 * project's "High Level Overview" dashboard block. Purely a UI grouping — the
 * "General" tab is not a row here, it's the implicit `tabId: null` bucket,
 * always shown first and not deletable.
 */
export interface ProjectDashboardCardTab extends BaseTenantDataModel {
  projectId: number;
  name: string;
  /** MUI icon name — frontend concern only, same convention as AiAssistantRole.avatarIcon. */
  icon: string | null;
  order: number;
}

/**
 * Marks an entity type as "managed project knowledge" — shown in the
 * console's MANAGED KNOWLEDGE sidebar block, where every project entity of
 * this type is browsable as its own view. `entityType` matches
 * `Entity.entityType`.
 */
export interface ProjectManagedEntityType extends BaseTenantDataModel {
  projectId: number;
  entityType: string;
  /** Sidebar display order — lower first. */
  order: number;
}

/**
 * One AI-generated "insight topic" for a ProjectManagedEntityType — e.g. for
 * a COMPETITOR type: feature comparison, pricing model, market position.
 * Produced by the GENERATE_ENTITY_TYPE_ENRICHMENT job from a single selected
 * EntityTypeSkill (`topicTitle` = skill name); each run replaces only the row
 * for (projectId, entityType, skillId, entityId). The body lives in
 * EntityTypeEnrichmentSection rows, one per Markdown heading.
 */
export interface EntityTypeEnrichment extends BaseTenantDataModel {
  projectId: number;
  entityType: string;
  /** EntityTypeSkill.id this topic was generated from. */
  skillId: number | null;
  /** The one Entity this topic is about, for an ENTITY-scope EntityTypeSkill — null for an ENTITY_TYPE-scope skill's topic. */
  entityId: number | null;
  topicTitle: string;
  topicDescription: string | null;
  topicSources: DocumentCitation[] | null;
  order: number;
}

/**
 * One heading of an EntityTypeEnrichment topic — a tree like DocumentSection:
 * `parentSectionId` is the nearest preceding shallower heading, null for a
 * root section; `content` is the Markdown under the heading, null when it
 * only groups sub-sections; `order` is the heading's position in the topic.
 */
export interface EntityTypeEnrichmentSection extends BaseTenantDataModel {
  projectId: number;
  entityTypeEnrichmentId: number;
  parentSectionId: number | null;
  title: string;
  content: string | null;
  order: number;
}

export type EntityMarketFeedImportance = 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * One web result (news article, blog post, or social post) about a project
 * entity, found via web search within roughly the past week. Produced by the
 * GENERATE_ENTITY_MARKET_FEED job — each run replaces every existing row for
 * (projectId, entityType).
 */
export interface EntityMarketFeed extends BaseTenantDataModel {
  projectId: number;
  entityType: string;
  /** Entity.entityIdentifier. */
  entityIdentifier: string;
  entityName: string;
  resourceName: string;
  publishedAt: string;
  description: string;
  url: string;
  importance: EntityMarketFeedImportance;
}

export type EntityTypeSkillStatus = 'ACTIVE' | 'DISABLED';
/** Recommended interval to re-run this skill. ON_DEMAND = manual only, no schedule. */
export type EntityTypeSkillCadence = 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ON_DEMAND';
/** ENTITY_TYPE (default): runs over every entity of the type at once. ENTITY: runs over one entity the caller selects — see MainTab's entity-picker dialog. */
export type EntityTypeSkillScope = 'ENTITY_TYPE' | 'ENTITY';

/** An instructional skill fragment attached to one EntityType catalog entry — see ManagedEntityTypeDetail's "Generate Insights" dropdown. */
export interface EntityTypeSkill extends BaseDataModel {
  /** EntityType.name, joined by value. */
  entityType: string;
  name: string;
  description: string;
  content: string;
  order: number | null;
  status: EntityTypeSkillStatus;
  cadence: EntityTypeSkillCadence;
  scope: EntityTypeSkillScope;
}

export interface Item extends BaseTenantDataModel {
  projectId: number;
  name: string;
  description: string | null;
}

export interface ItemDetail extends BaseTenantDataModel {
  itemId: number;
  notes: string | null;
  priority: number;
}

export interface ItemTag extends BaseTenantDataModel {
  itemId: number;
  tagId: number;
  /** Eager-loaded when listing /items/:itemId/tags. */
  tag?: Tag;
}

// --- global catalog ---------------------------------------------------------

export interface ProjectGeneratePromptTemplate extends BaseDataModel {
  name: string;
  label: string;
  description: string | null;
  content: string;
  order: number | null;
}

// --- docs domain ------------------------------------------------------------

export enum DocumentStatusEnum {
  IN_GENERATION = 'IN_GENERATION',
  DRAFT = 'DRAFT',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  ARCHIVED = 'ARCHIVED',
}

export interface DocumentCategory extends BaseDataModel {
  name: string;
  type: string;
  description: string | null;
  order: number | null;
}

export type AiAssistantRoleStatus = 'Active' | 'Inactive';

/** A role-based, domain-specialized AI Assistant persona (docs/AI-Assistant-Roles.md). */
export interface AiAssistantRole extends BaseDataModel {
  key: string;
  name: string;
  description: string;
  avatarIcon: string | null;
  color: string | null;
  logoImage: string | null;
  documentCategoryType: string;
  order: number | null;
  status: AiAssistantRoleStatus;
}

export type DocumentTypeImportance = 'HIGH' | 'MEDIUM' | 'LOW';
export type DocumentTypeStatus = 'Active' | 'Disabled';

export interface DocumentType extends BaseDataModel {
  name: string;
  label: string;
  type: string;
  description: string | null;
  categoryType: string;
  parentDocumentType: string | null;
  importance: DocumentTypeImportance;
  order: number | null;
  status: DocumentTypeStatus;
  dependentDocumentTypes: string[];
  relatedSections: string[];
}

/** One web-search source cited while generating a document's or section's content. */
export interface DocumentCitation {
  title: string;
  url: string;
  publishedDate?: string;
}

export interface Document extends BaseTenantDataModel {
  projectId: number;
  name: string;
  description: string | null;
  documentType: string;
  status: DocumentStatusEnum;
  version: string;
  importance: string;
  parentDocumentId: number | null;
  createdByUserId: number | null;
  lastUpdatedByUserId: number | null;
  generateUserPrompt: string | null;
  /** The user's answers to the clarifying questions asked before this document was generated. Skipped questions are omitted. */
  generateQuestionsAnswers: DocumentQuestionAnswer[] | null;
  aiSummary: string | null;
  citations: DocumentCitation[] | null;
  /** The LlmRequest row of the call that generated this document's content. */
  llmRequestId: number | null;
  /** Populated when fetched with include=documentType */
  documentTypeData?: DocumentType;
}

export type DocumentSectionStatus = 'BACKLOG' | 'IN_PROGRESS' | 'IN_REVIEW' | 'APPROVED' | 'ARCHIVED';
export type DocumentSectionConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

export interface DocumentSection extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  documentSectionType: string;
  parentDocumentSectionId: number | null;
  title: string;
  description: string | null;
  order: number | null;
  content: string | null;
  contentDraft: string | null;
  summary: string | null;
  aiSummary: string | null;
  status: DocumentSectionStatus;
  importance: string;
  /** How positive (10) or negative (1) the section's content is for the product/solution. Integer 1–10. */
  verdictScore: number;
  confidence: DocumentSectionConfidence;
  /** One short sentence on why the section got its verdictScore; null until scored. */
  verdictReason: string | null;
  createdByUserId: number | null;
  lastUpdatedByUserId: number | null;
  assignUserId: number | null;
  /** Last visualization-generation job id (poll it to show progress / block re-runs). */
  visualizationJobId: number | null;
  citations: DocumentCitation[] | null;
}

export interface DocumentVisualization extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  documentSectionId: number | null;
  /** Base64 PNG. Omitted by the list endpoint; present on GET by id. */
  imageData?: string;
  mimeType: string;
  userInstructions: string | null;
  model: string | null;
  sourceUpdatedAt: string | null;
  /** The visualization representing its section. Only the user can move it. */
  isDefault: boolean;
  createdByUserId: number | null;
}

export type DocumentDashboardCardType = 'NUMERIC' | 'TOP_LIST' | 'TABLE' | 'PROGRESS_PERCENT';

export interface DocumentDashboardCard extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  /** Null for a document level card. */
  documentSectionId: number | null;
  /** Ids of the DocumentSection rows whose content grounds this card's fact(s). */
  sourceDocumentSectionIds: number[];
  cardType: DocumentDashboardCardType;
  cardTitle: string;
  cardDescription: string | null;
  cardNumberValue: string | null;
  cardNumberTotalValue: string | null;
  cardListValue: string[] | null;
  order: number;
  isPinned: boolean;
  /** How many of the Dashboard tab's 4 grid columns this card spans (1-4). Null = not resized yet, auto width by cardType — see dashboardCardColumnSpan. */
  columnSpan: number | null;
}

export type DocumentPresentationStatus = 'IN_GENERATION' | 'READY' | 'FAILED';

export type DocumentPresentationSlideType =
  | 'COVER'
  | 'OVERVIEW'
  | 'SECTION'
  | 'GROUP'
  | 'RELATIONS'
  | 'SUMMARY';

/** A slide-deck visualization of a whole document. */
export interface DocumentPresentation extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  title: string;
  status: DocumentPresentationStatus;
  userInstructions: string | null;
  model: string | null;
  /** The generating job — poll it while the status is IN_GENERATION. */
  jobId: number | null;
  createdByUserId: number | null;
}

export interface DocumentPresentationSlide extends BaseTenantDataModel {
  projectId: number;
  presentationId: number;
  orderIndex: number;
  slideType: DocumentPresentationSlideType;
  title: string;
  /** Short markdown — a few bullets. */
  body: string | null;
  documentSectionId: number | null;
  /** Fetch the image itself from `/document-visualizations/:id`. */
  documentVisualizationId: number | null;
}

/** Shape returned by GET /document-presentations/:id. */
export interface DocumentPresentationWithSlides extends DocumentPresentation {
  slides: DocumentPresentationSlide[];
}

// --- idea validation summary ------------------------------------------------
// Mirrors backend/src/data-models/project/project-validation-summary.ts.

export type ProjectValidationSummaryStatus = 'IN_GENERATION' | 'READY' | 'FAILED';
/** The founder's own call, recorded on the summary step. */
export type ValidationDecision = 'GO' | 'PIVOT' | 'NO_GO';
export type ValidationRecommendation = 'GO' | 'CONDITIONAL_GO' | 'PIVOT' | 'NO_GO';
export type ValidationVerdictLabel = 'Validated' | 'Partially validated' | 'Invalidated' | 'Unknown';
export type ValidationLevel = 'HIGH' | 'MEDIUM' | 'LOW';

/** One stage question: a root section of the stage document, with its stored verdict score. */
export interface ValidationSummaryQuestion {
  sectionId: number;
  sectionType: string;
  title: string;
  score: number;
  confidence: DocumentSectionConfidence;
  verdictLabel: ValidationVerdictLabel | null;
  verdictReason: string | null;
}

export interface ValidationSummaryStage {
  /** The stage's document type — the key the validation page selects stages by. */
  docType: string;
  name: string;
  documentId: number;
  /** Mean of the questions' scores, one decimal. */
  score: number;
  /** The weakest confidence among the questions. */
  confidence: DocumentSectionConfidence;
  questions: ValidationSummaryQuestion[];
}

/** The part of the summary the job computes from existing rows — no LLM involved. */
export interface ValidationSummaryComputed {
  version: 1;
  overallScore: number;
  bandCounts: { strong: number; mixed: number; weak: number };
  allowedRecommendations: ValidationRecommendation[];
  evidence: { questions: number; sources: number; assumptions: number };
  stages: ValidationSummaryStage[];
}

/** The part written by the summary LLM call, validated by the job. Digests are absent when the stages do not support them. */
export interface ValidationSummaryNarrative {
  recommendation: ValidationRecommendation;
  confidence: DocumentSectionConfidence;
  headline: string;
  rationale: string;
  /** What must be true to turn the recommendation into a plain GO; empty for GO and NO_GO. */
  conditions: string[];
  valueStatement: string | null;
  /** One line per stage, keyed by docType. */
  stageVerdicts: Record<string, string>;
  /** One sentence per question, keyed by section id. */
  questionTakeaways: Record<string, string>;
  strengths: Array<{ text: string; docType: string }>;
  risks: Array<{ text: string; docType: string }>;
  metrics: Array<{ label: string; value: string; detail: string; docType: string }>;
  experiments: Array<{ assumption: string; test: string; passBar: string; docType: string }>;
  market?: {
    sizes: Array<{ label: string; value: string; detail: string }>;
    growth: Array<{ label: string; pct: number }>;
    demandSignals: string[];
  };
  economics?: {
    scenarios: string[];
    rows: Array<{ metric: string; values: string[] }>;
    ltvCac: number[] | null;
    breakPoint: string | null;
  };
  customer?: {
    primary: string;
    description: string;
    user: string;
    buyer: string;
    segmentSize: string;
    reachability: string;
    switchLikelihood: Array<{ segment: string; likelihood: ValidationLevel }>;
  };
  competition?: {
    competitors: Array<{ name: string; category: string; price: string; threat: ValidationLevel }>;
    todayMix: Array<{ label: string; pct: number }> | null;
    unmetNeeds: string[];
  };
  gtm?: {
    motion: string;
    funnel: Array<{ label: string; value: string }>;
    salesCycle: string;
    trigger: string;
  };
  /** Business validation only: what the company has actually achieved, from the Traction & Growth stage. */
  traction?: {
    customers: string;
    revenue: string;
    growth: string;
    retention: string;
    funding: string;
    momentum: string;
    signals: Array<{ label: string; value: string; detail: string }>;
  };
  /** Business validation only: the valuation frame and the deal's exposure, from the Risks & Deal stage. */
  deal?: {
    valuationRange: string | null;
    comparables: Array<{ name: string; multiple: string; detail: string }>;
    exitPaths: string[];
    /** HIGH = deal-breaker, MEDIUM = serious, LOW = minor. */
    redFlags: Array<{ text: string; severity: ValidationLevel }>;
  };
  /** Stock validation only: the price frame for the buy / hold / sell call. */
  stock?: {
    price: string;
    horizon: string;
    fairValue: string | null;
    buyBelow: string | null;
    sellAbove: string | null;
    scenarios: Array<{ label: string; value: string; detail: string }>;
    catalysts: Array<{ text: string; timing: string }>;
    /** What would change the verdict: to BUY, HOLD or SELL, and the signal to watch. */
    triggers: Array<{ condition: string; changeTo: string; signal: string }>;
  };
  truncated: boolean;
}

export type ProjectValidationSummaryData = ValidationSummaryComputed & ValidationSummaryNarrative;

/**
 * The go / no-go summary of a project's idea validation: one row per
 * project, rewritten in place on regeneration. The founder's decision lives
 * on the same row and survives regenerations.
 */
export interface ProjectValidationSummary extends BaseTenantDataModel {
  projectId: number;
  status: ProjectValidationSummaryStatus;
  /** The generating job — poll it while the status is IN_GENERATION. */
  jobId: number | null;
  model: string | null;
  recommendation: ValidationRecommendation | null;
  overallScore: number | null;
  data: ProjectValidationSummaryData | null;
  /** The stage documents the summary was built from, and the newest of their updatedAt at that moment. */
  sourceDocumentIds: number[];
  sourceUpdatedAt: string | null;
  decision: ValidationDecision | null;
  decisionNote: string | null;
  decidedAt: string | null;
  decidedByUserId: number | null;
}

// --- tasks domain -----------------------------------------------------------

export type TaskPriority = 'LOW' | 'MEDIUM' | 'HIGH';

export interface Task extends BaseTenantDataModel {
  projectId: number;
  title: string;
  description: string | null;
  relatedDocumentId: number | null;
  assignUserId: number | null;
  priority: TaskPriority;
  dueTime: string | null;
}

// --- document type sections (global catalog) --------------------------------

export interface DocumentTypeSection extends BaseDataModel {
  title: string;
  type: string;
  description: string | null;
  documentType: string;
  parentDocumentTypeSectionType: string | null;
  defaultOrder: number | null;
  importance: string;
  visualPriority: string | null;
  status: string;
}

export interface EntityType extends BaseDataModel {
  name: string;
  description: string | null;
}

/** M:N relation — which EntityType catalog entries are relevant for a given document type. */
export interface DocumentTypeEntityType extends BaseDataModel {
  documentTypeId: string;
  entityTypeId: number;
  isActive: boolean;
}

// --- jobs -------------------------------------------------------------------

export type JobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
export type JobPriority = 'LOW' | 'MEDIUM' | 'HIGH';

export interface Job extends BaseTenantDataModel {
  jobType: string;
  status: JobStatus;
  priority: JobPriority;
  progressPercent: number;
  progressMessage: string | null;
  /** JSON-encoded, shape depends on jobType — e.g. `{ outputChars, outputTokens }` while an LLM call streams. */
  progressAdditionalData: string | null;
  data: string | null;
  result: string | null;
  error: string | null;
  projectId: number | null;
  userId: number | null;
  /** The job created in direct response to this one (e.g. GENERATE_DOCUMENT after questions were answered). Null = not yet acted on. */
  resolvedByJobId: number | null;
}

/** A clarifying question asked before a document is generated. */
export interface DocumentQuestion {
  id: string;
  question: string;
  why?: string;
  suggestedAnswer?: string;
}

/** A question the user chose to answer. Skipped questions are omitted. */
export interface DocumentQuestionAnswer {
  question: string;
  answer: string;
}

/** Shape of `Job.result` for a GENERATE_DOCUMENT_QUESTIONS job. */
export interface GenerateQuestionsJobResult {
  documentType: string;
  questions: DocumentQuestion[];
  /** Present when no questions were needed and the generate job was chained. */
  generateJobId?: number;
}

// --- project entities / relations -------------------------------------------

/** A (source, key, value) fact about an entity. One row per (entity, source, key). */
export interface EntityProperty extends BaseTenantDataModel {
  projectId: number;
  entityId: number;
  /** What produced this fact — a job type, an external system, or USER for a manual edit. */
  source: string;
  key: string;
  value: string;
}

/**
 * A project entity — one row per real-world thing per project, keyed by
 * `entityIdentifier`. Source-agnostic: document section extraction is one
 * producer (see `DocumentSectionEntity`, the link from a section to the entity
 * it mentions), but a row can equally come from a research job or an import.
 */
export interface Entity extends BaseTenantDataModel {
  projectId: number;
  entityIdentifier: string;
  entityName: string;
  entityType: string;
  entityDescription: string | null;
  /** User-curated flag, set via `resources.setEntityManaged`. */
  isManaged: boolean;
  /** Eager-loaded when fetched with include=properties */
  properties?: EntityProperty[];
}

/** A (source, key, value) fact about an entity relation. */
export interface EntityRelationProperty extends BaseTenantDataModel {
  projectId: number;
  entityRelationId: number;
  source: string;
  key: string;
  value: string;
}

/** A relation between two project entities — the project-level counterpart of `Entity`. */
export interface EntityRelation extends BaseTenantDataModel {
  projectId: number;
  sourceEntityId: number;
  targetEntityId: number;
  relationType: string;
  relationDescription: string | null;
  /** Eager-loaded when fetched with include=sourceEntity */
  sourceEntity?: Entity;
  /** Eager-loaded when fetched with include=targetEntity */
  targetEntity?: Entity;
  /** Eager-loaded when fetched with include=properties */
  properties?: EntityRelationProperty[];
}

// --- document section links to those ----------------------------------------

/**
 * Links a document section to the project `Entity` it mentions. What the entity
 * IS lives on `Entity`; only what is specific to this mention lives here.
 */
export interface DocumentSectionEntity extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  /**
   * The section whose own content mentions the entity. Null for a
   * document-level link written by the generate-document job: the document
   * as a whole describes the entity, no single section.
   */
  documentSectionId: number | null;
  /**
   * Id of the top-level (root) section `documentSectionId` belongs to (a
   * root section's own value here is its own id). Null on a row extracted
   * before this field existed.
   */
  rootDocumentSectionId: number | null;
  entityId: number;
  /**
   * Sentence or part-of-sentence, copied verbatim from the section's own
   * content, where the entity is named or described (first appearance only
   * when the entity is mentioned more than once in the section).
   */
  spanText: string | null;
  /** Eager-loaded when fetched with include=entity */
  entity?: Entity;
}

/** Links a document section to the `EntityRelation` its extraction asserted. */
export interface DocumentSectionRelation extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  documentSectionId: number;
  entityRelationId: number;
  /** Eager-loaded when fetched with include=entityRelation */
  entityRelation?: EntityRelation;
}

// --- document access --------------------------------------------------------

export type DocumentAccessType = 'VIEW' | 'EDIT';

export interface DocumentAccess extends BaseTenantDataModel {
  documentId: number;
  userId: number;
  accessType: DocumentAccessType;
}

// --- project sharing: roles, members, invitations ---------------------------

/**
 * What a user may do inside one project. Mirrors the backend `ProjectRole`
 * enum; the capability matrix lives in `core/services/permissions.ts`.
 */
export type ProjectRole = 'OWNER' | 'REVIEWER' | 'VIEWER';

export type ProjectMemberStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED';

/** A row of `GET /projects/:projectId/members`. */
export interface ProjectMember {
  id: number;
  userId: number;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: ProjectRole;
  status: ProjectMemberStatus;
  invitedEmail: string;
  /** Display name set by the inviter; superseded by `name` once accepted. */
  invitedName: string | null;
  invitedBy: { id: number; name: string | null; email: string };
  /** null = access that never expires. */
  expiresAt: string | null;
  acceptedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/**
 * A row of `GET /my/projects` — every project the caller can reach, across all
 * workspaces. `access` is what the UI keys the "shared" marking off; it comes
 * from the server rather than being inferred by comparing tenant ids.
 */
export interface AccessibleProject {
  id: number;
  name: string;
  description: string | null;
  projectType: ProjectType;
  tenantId: number;
  tenantName: string;
  /** 'own' = a workspace the caller belongs to. 'shared' = granted per-project. */
  access: 'own' | 'shared';
  role: ProjectRole;
  /** Present only when `access === 'shared'`. */
  sharedBy?: { name: string | null; email: string };
  sharedAt?: string | null;
}

/** `GET /invitations/preview` — what the landing page renders before sign-in. */
export interface InvitationPreview {
  projectName: string;
  tenantName: string;
  invitedEmail: string;
  invitedByName: string;
  role: ProjectRole;
  expiresAt: string | null;
  status: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
}

// --- comments ---------------------------------------------------------------

export interface DocumentComment extends BaseTenantDataModel {
  projectId: number;
  documentId: number;
  documentSectionId: number | null;
  parentId: number | null;
  authorUserId: number;
  body: string;
  resolvedAt: string | null;
  resolvedByUserId: number | null;
}

// --- search ------------------------------------------------------------------

export interface SearchVisualizationResult {
  id: number;
  documentId: number;
  documentSectionId: number | null;
  mimeType: string;
}

export interface SearchEntityResult {
  id: number;
  entityName: string;
  entityType: string;
  entityDescription: string | null;
  documentId: number;
  documentName: string;
  /** Null for a document-level link — see DocumentSectionEntity.documentSectionId. */
  documentSectionId: number | null;
  documentSectionTitle: string | null;
}

export interface SearchSectionResult {
  documentId: number;
  documentName: string;
  documentSectionId: number;
  documentSectionTitle: string;
  documentSectionAiSummary: string | null;
  documentSectionContent: string | null;
}

export interface SearchDocumentResult {
  documentId: number;
  documentName: string;
  documentType: string;
  documentDescription: string | null;
  documentAiSummary: string | null;
}

export interface TimingNode {
  label: string;
  durationMs: number;
  children?: TimingNode[];
}

export interface SearchResponse {
  query: string;
  visualizations: SearchVisualizationResult[];
  entities: SearchEntityResult[];
  sections: SearchSectionResult[];
  documents: SearchDocumentResult[];
  timing: TimingNode;
}

/** Which search strategies to run. At least one must stay enabled. */
export interface SearchOptions {
  useFullText: boolean;
  useRag: boolean;
}
